<script setup>
import {
    computed, onMounted, ref, watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import DayPicker from "@/modules/management-panel/components/DayPicker/DayPicker.vue";
import SessionList from "./SessionList.vue";
import AddNewEvent from "./AddNewEvent.vue";
import ButtonComponent from "@/components/Button/ButtonComponent.vue";
import { useMainStore } from "../../../../stores/mainStore";

const emit = defineEmits(["changeDay", "update", "edit"]);
const props = defineProps({
    venues: {
        type: Array,
        default: () => [],
    },
    repetition: {
        type: String,
        default: "",
    },
    termData: {
        type: Object,
        default: () => {
        },
    },
    openThisVenueItem: {
        type: Number,
        default: 0,
    },
});

const isOpen = ref(0);
const disabledDays = ref([]);
// eslint-disable-next-line no-unused-vars
const mainStore = useMainStore();
const router = useRouter();
const route = useRoute();

function getVenueSelectedDay(venueKey, venueList, defaultDay = "mon") {
    if (props.termdate?.recurrence === "Daily recurrence") {
        return "";
    }

    return route.params.weekday ? route.params.weekday : defaultDay;
}

function calculateDisabledDays() {
    const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const disabledDaysByVenues = [];
    props.venues.forEach((venue) => {
        const { sessions } = venue;
        const foundDays = sessions.map((session) => session.day?.substring(0, 3));

        const resultDays = days.filter((day) => foundDays.includes(day) === false);

        disabledDaysByVenues.push(resultDays);
    });

    disabledDays.value = disabledDaysByVenues;
}

function changeDay(arg1, arg2) {
    emit("changeDay", { event: arg1, key: arg2 });
}

function setVenueOpen(idx) {
    // eslint-disable-next-line no-return-assign
    // Object.keys(isOpen.value).map((single) => (isOpen.value[single] = false));
    isOpen.value = idx;
}

function toggleVenue(venueKey) {
    setVenueOpen(venueKey);

    if (isOpen.value === venueKey) {
        router.push({ params: { venueId: props.venues[venueKey].venue_id, weekday: "" } });
    }
    // Adding settimeout to recalculate disabled days when opening location dropdown
    setTimeout(() => {
        calculateDisabledDays();
    });
}

function updateCurrentData(data) {
    emit("update", data);
}

onMounted(() => {
    calculateDisabledDays();
    if (route.params.venueId !== "") {
        const idx = props.venues.findIndex((v) => v.venue_id === route.params.venueId);
        setVenueOpen(idx);
    }
});

watch(
    () => props.venues,
    () => {
        calculateDisabledDays();
        if (route.params.venueId !== "") {
            const idx = props.venues.findIndex((v) => v.venue_id === route.params.venueId);
            setVenueOpen(idx);
        }
    },
);

function formattedVenueAddress(venue) {
    const {
        name,
        address,
        city,
        postcode,
    } = venue;

    const fullAddress = `${name}, ${address}, ${city}, ${postcode}`;
    const okLength = 65;
    if (fullAddress.length <= okLength) {
        return fullAddress;
    }

    return `${fullAddress.slice(0, okLength)}...`;
}

const getFormattedHolidays = computed(() => {
    const holidays = mainStore.holidays.filter(
        (h) => h.termdate_id === props.termData.termId,
    );

    return holidays.map((h) => ({
        start: {
            plain: h.start,
        },
        end: {
            plain: h.end,
        },
        oneDay: h.one_day === "Y",
    }));
});
</script>

<template>
    <div v-if="venues" class="mt-4" data-test-id="venues-list">
        <div
            v-for="(venue, key) in venues"
            :key="venue.venue_id"
        >
            <div class="venue-title-bar d-flex align-items-center" :data-test="`venue-${key}`">
                <div
                    class="venue-title"
                    :data-test-id="`venue-title-${key}`"
                    data-test="venue-title"
                    v-html="formattedVenueAddress(venue)"
                ></div>
                <div class="venue-action-buttons">
                    <ButtonComponent
                        @click="emit('edit', venue.venue_id)"
                        dataTest="btn-venue-edit"
                        type="sub"
                        class="ml-3"
                    >
                        Edit
                    </ButtonComponent>
                    <ButtonComponent
                        @click="toggleVenue(key)"
                        class="ml-auto"
                        dataTest="btn-venue-hide-show"
                        type="sub"
                    >
                        <span v-if="isOpen !== key">
                            Show
                            <svg
                                class="show-arrow"
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clip-path="url(#clip0_119_4957)">
                                    <path
                                        d="M14.9854 7.51465C14.9854 3.3727 11.6273 0.0146483 7.48535 0.0146481C3.34341 0.0146479 -0.0146467 3.3727 -0.0146469 7.51465C-0.014647 11.6566 3.34341 15.0146 7.48535 15.0147C11.6273 15.0147 14.9854 11.6566 14.9854 7.51465ZM4.43163 8.78785C4.37859 8.84367 4.31491 8.88832 4.24434 8.91915C4.17378 8.94998 4.09776 8.96637 4.02076 8.96735C3.94376 8.96834 3.86734 8.9539 3.79601 8.92489C3.72468 8.89587 3.65987 8.85288 3.60542 8.79842C3.55097 8.74397 3.50797 8.67917 3.47896 8.60784C3.44995 8.53651 3.43551 8.46009 3.43649 8.38309C3.43748 8.30609 3.45387 8.23007 3.4847 8.1595C3.51553 8.08894 3.56017 8.02526 3.616 7.97222L7.07754 4.51068C7.18572 4.40257 7.33241 4.34184 7.48535 4.34184C7.6383 4.34184 7.78498 4.40257 7.89316 4.51068L11.3547 7.97222C11.4583 8.0813 11.5152 8.22654 11.5133 8.37698C11.5114 8.52742 11.4508 8.67115 11.3444 8.77754C11.238 8.88392 11.0943 8.94454 10.9438 8.94647C10.7934 8.94839 10.6482 8.89147 10.5391 8.78785L7.48535 5.73448L4.43163 8.78785Z"
                                        fill="#FFB700"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_119_4957">
                                        <rect
                                            width="15"
                                            height="15"
                                            fill="white"
                                            transform="translate(15) rotate(90)"
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                        </span>
                        <span v-if="isOpen === key">
                            Hide
                            <svg
                                class="hide-arrow"
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clip-path="url(#clip0_119_4957)">
                                    <path
                                        d="M14.9854 7.51465C14.9854 3.3727 11.6273 0.0146483 7.48535 0.0146481C3.34341 0.0146479 -0.0146467 3.3727 -0.0146469 7.51465C-0.014647 11.6566 3.34341 15.0146 7.48535 15.0147C11.6273 15.0147 14.9854 11.6566 14.9854 7.51465ZM4.43163 8.78785C4.37859 8.84367 4.31491 8.88832 4.24434 8.91915C4.17378 8.94998 4.09776 8.96637 4.02076 8.96735C3.94376 8.96834 3.86734 8.9539 3.79601 8.92489C3.72468 8.89587 3.65987 8.85288 3.60542 8.79842C3.55097 8.74397 3.50797 8.67917 3.47896 8.60784C3.44995 8.53651 3.43551 8.46009 3.43649 8.38309C3.43748 8.30609 3.45387 8.23007 3.4847 8.1595C3.51553 8.08894 3.56017 8.02526 3.616 7.97222L7.07754 4.51068C7.18572 4.40257 7.33241 4.34184 7.48535 4.34184C7.6383 4.34184 7.78498 4.40257 7.89316 4.51068L11.3547 7.97222C11.4583 8.0813 11.5152 8.22654 11.5133 8.37698C11.5114 8.52742 11.4508 8.67115 11.3444 8.77754C11.238 8.88392 11.0943 8.94454 10.9438 8.94647C10.7934 8.94839 10.6482 8.89147 10.5391 8.78785L7.48535 5.73448L4.43163 8.78785Z"
                                        fill="#FFB700"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_119_4957">
                                        <rect
                                            width="15"
                                            height="15"
                                            fill="white"
                                            transform="translate(15) rotate(90)"
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                        </span>
                    </ButtonComponent>
                </div>
            </div>
            <div v-if="isOpen === key" class="venue-events-list p-4">
                <div class="d-flex align-items-center mb-4 venue-events-list-container">
                    <DayPicker
                        v-if="venue.sessions && repetition === 'weekly'"
                        :day-selected="getVenueSelectedDay(key, venues)"
                        :disabled-days="disabledDays[key]"
                        @changeDay="changeDay($event, key)"
                    />
                    <AddNewEvent
                        :term-data="termData"
                        :holidays="getFormattedHolidays"
                        :repetition="repetition"
                        :venue-id="venue.venue_id"
                        :selected-day="route.params.weekday ?? 'mon'"
                        @update="updateCurrentData"
                    />
                </div>
                <SessionList
                    v-if="venue.sessions"
                    :sessions="venue.sessions"
                    :selected-day="getVenueSelectedDay(key, venues)"
                    :venue-id="venue.venue_id"
                    :term-id="termData.termId"
                    :repetition="repetition"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
.venue-title {
    font-weight: 600;
    font-size: 1.125rem;
    line-height: 1.6875rem;
    max-width: 85%;
}

.venue-action-buttons {
    max-width: 15%;
    display: flex;
    justify-content: space-between;
    margin-left: auto;
}

.venue-action-buttons > button {
    margin-right: 10px;
}

.venue-title-bar {
    border-top: 1px solid #DDDDDD;
    border-bottom: 1px solid #DDDDDD;
    padding-top: 7px;
    padding-bottom: 7px;
    width: 100%;
}

.venue-events-list {
    background-color: #F6F6F6;
}

.btn-new {
    font-size: 12px;
    padding: 10px 24px;
}

.btn-sub {
    padding: 0;
    height: max-content;
    display: inline;
    background-color: white;
    border: 0;
    font-weight: 900;
    color: #260045;
}

.btn-sub:hover {
    text-decoration: underline;
}

.show-arrow {
    transform: rotate(180deg);
}

@media only screen and (max-width: 768px) {
    .venue-title-bar {
        flex-wrap: wrap;
    }

    .venue-action-buttons {
        width: 100%;
        max-width: 100%;
    }

    .venue-events-list-container {
        flex-wrap: wrap;
    }

    .venue-events-list-container button {
        width: 100%;
    }
}
</style>
